import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import MicDictation from "../components/MicDictation";
import PastoralProgress from "../components/PastoralProgress";
import PageMeta from "../components/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Discernment() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    people_involved: "",
    situation_description: "",
    conversation_text: "",
    background_context: "",
    user_question: "",
    screenshot_roles: "",
    desired_depth: "standard",
  });
  const [files, setFiles] = useState([]);
  // Relational cross-read uploads (Phase 2). Either or both may be set.
  // The "their" packet requires explicit consent affirmation before the
  // backend will accept it.
  const [yourPacket, setYourPacket] = useState(null);
  const [theirPacket, setTheirPacket] = useState(null);
  const [theirConsent, setTheirConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Always land at the top of the page on entry.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (next) => setForm((f) => ({ ...f, [k]: next }));

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list.slice(0, 6)); // safety cap
  };

  const [serverPhase, setServerPhase] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setServerPhase("");
    if ((form.situation_description || "").trim().length < 20) {
      setErr("Please describe the situation in at least 20 characters.");
      return;
    }
    if (theirPacket && !theirConsent) {
      setErr("Please affirm that the other party shared their packet willingly before we read it.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      files.forEach((f) => fd.append("screenshots", f));
      if (yourPacket) fd.append("your_packet", yourPacket);
      if (theirPacket) fd.append("their_packet", theirPacket);
      fd.append("their_packet_consent", theirConsent ? "true" : "false");
      // Kick off the background job. The form upload is the only
      // long-ish payload (screenshots up to 8 MB each) but the OpenAI
      // call is OFF this request thread now, so the production
      // ingress 30s timeout no longer kills the submission.
      const startRes = await axios.post(`${API}/discernment/start`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const jobId = startRes.data.job_id;
      setServerPhase("Reading the situation you brought…");
      const started = Date.now();
      // Poll every 2s up to ~4 minutes.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await axios.get(`${API}/discernment/job/${jobId}`, { timeout: 15000 });
        if (poll.data.phase) setServerPhase(poll.data.phase);
        if (poll.data.status === "ready" && poll.data.package_id) {
          navigate(`/discernment/${poll.data.package_id}`);
          return;
        }
        if (poll.data.status === "error") {
          throw new Error(poll.data.error || "The Matrix stumbled while discerning. Please try again.");
        }
        if (Date.now() - started > 240000) {
          throw new Error("This is taking longer than expected. Please try again.");
        }
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Something went wrong. Please try again.");
      setSubmitting(false);
      setServerPhase("");
    }
  };

  return (
    <main className="relative z-10 min-h-screen flex flex-col" data-testid="discernment-page">
      <PageMeta
        title="Spiritual Warfare Engine"
        description="Bring one relationship, conflict, or household tension into the light. The Spiritual Warfare Engine reads your situation and returns a Christ-centered Discernment Packet — what is operating, how it moves, and how to answer it."
        path="/discernment"
      />
      <header className="relative z-20 flex items-center justify-between px-6 sm:px-12 md:px-20 py-6">
        <Link to="/" className="font-cinzel text-[0.78rem] sm:text-sm text-[#FFE7A0] font-semibold" data-testid="disc-brand">
          THE RESTORATION MATRIX
        </Link>
        <Link to="/" className="rm-btn-ghost" data-testid="disc-nav-home">Home</Link>
      </header>

      <div className="relative z-20 px-6 sm:px-12 md:px-20 -mt-2 mb-2 flex justify-center">
        <span className="rm-privacy-inline" data-testid="discernment-privacy-disclaimer">
          <span className="rm-privacy-dot" aria-hidden="true" />
          Privacy guaranteed — no record of anything is saved (your screenshots and inputs included), no account needed.
        </span>
      </div>

      <section className="relative z-10 flex-1 w-full max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="overline mb-3">Discernment Engine</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#F0E6D2] mb-4">
            Bring the situation into the light.
          </h1>
          <p className="text-[#F0E6D2] text-base sm:text-lg font-body font-light leading-relaxed mb-10">
            Describe a relational situation, conflict, conversation, or household
            tension. You can upload screenshots — the Matrix will read them.
            You'll receive a Discernment Packet: what is operating
            spiritually, how to walk in the Spirit of Love, and a clean response.
          </p>

          {err ? (
            <div className="mb-6 p-4 border border-red-700/40 bg-red-950/30 text-red-200 text-sm rounded" data-testid="disc-error">
              {err}
            </div>
          ) : null}

          <form onSubmit={submit} className="space-y-8">
            <div
              className="border border-[#FFE7A0]/30 bg-[#FFE7A0]/5 px-5 py-3 text-sm text-[#F0E6D2]/90 font-body font-light leading-relaxed"
              data-testid="disc-input-quality-note"
            >
              <span className="font-cinzel text-[0.7rem] tracking-[0.22em] uppercase text-[#FFE7A0] mr-2">Note</span>
              The more detail you share below, the more thorough and accurate the discernment. Voice input is welcome — tap the mic beside any field.
            </div>

            <Field label="People involved" hint="Names and roles. e.g., 'Robbie (homeowner), Jessica (housemate), Jake (traveler)'">
              <div className="relative">
                <input
                  type="text"
                  value={form.people_involved}
                  onChange={update("people_involved")}
                  className="rm-input w-full pr-12"
                  placeholder="Robbie, Jessica, Jake"
                  data-testid="disc-people"
                />
                <MicDictation
                  value={form.people_involved}
                  onTranscript={setField("people_involved")}
                  testId="disc-people-mic"
                />
              </div>
            </Field>

            <Field label="What's the situation?" required hint="What happened, what's happening now, what's the tension? Be specific.">
              <div className="relative">
                <textarea
                  value={form.situation_description}
                  onChange={update("situation_description")}
                  rows={6}
                  className="rm-input w-full pr-12"
                  placeholder="A traveler has been staying in Robbie's home. He's overstayed and left the house messy. Jessica wants him out immediately. Robbie feels guilty about putting someone on the street but is frustrated too…"
                  data-testid="disc-situation"
                />
                <MicDictation
                  value={form.situation_description}
                  onTranscript={setField("situation_description")}
                  testId="disc-situation-mic"
                />
              </div>
              <p className="mt-2 text-xs text-[#FFE7A0]/80 italic leading-relaxed" data-testid="disc-situation-quality-note">
                The richer this story, the sharper the discernment. Names, ages, roles, specific words spoken, how it has escalated — all of it helps.
              </p>
            </Field>

            <Field label="Paste any conversation text" hint="Optional. Quotes, texts, a thread — in chronological order if possible.">
              <div className="relative">
                <textarea
                  value={form.conversation_text}
                  onChange={update("conversation_text")}
                  rows={4}
                  className="rm-input w-full pr-12"
                  placeholder="Jessica: 'He needs to be out tonight.'  Robbie: 'Where's he gonna go?'  Jessica: '…'"
                  data-testid="disc-conversation"
                />
                <MicDictation
                  value={form.conversation_text}
                  onTranscript={setField("conversation_text")}
                  testId="disc-conversation-mic"
                />
              </div>
            </Field>

            <Field label="Screenshots" hint="Optional. Upload up to 6 images of the conversation. The Matrix will read them automatically.">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onFiles}
                className="block text-sm text-[#F0E6D2]"
                data-testid="disc-files"
              />
              {files.length > 0 ? (
                <div className="mt-2 text-xs text-[#F0E6D2]/75">
                  {files.length} image{files.length !== 1 ? "s" : ""} ready: {files.map(f => f.name).join(", ")}
                </div>
              ) : null}
            </Field>

            {files.length > 0 && (
              <Field label="Who is who in the screenshots?" hint="Optional. Tell the Matrix which side of the screen is whom so it doesn't guess wrong. A short note is enough.">
                <div className="relative">
                  <input
                    type="text"
                    value={form.screenshot_roles}
                    onChange={update("screenshot_roles")}
                    className="rm-input w-full pr-12"
                    placeholder="Right side (blue) = Robbie (me). Left side (gray) = Jessica."
                    data-testid="disc-screenshot-roles"
                  />
                  <MicDictation
                    value={form.screenshot_roles}
                    onTranscript={setField("screenshot_roles")}
                    testId="disc-screenshot-roles-mic"
                  />
                </div>
              </Field>
            )}

            <Field label="Background context" hint="Optional. Anything the Matrix should know about history, dynamics, or what's already been tried.">
              <div className="relative">
                <textarea
                  value={form.background_context}
                  onChange={update("background_context")}
                  rows={3}
                  className="rm-input w-full pr-12"
                  placeholder="The house has taken in several travelers over the years. Jessica has been quieter than usual lately. Jake's been homeless off and on for months."
                  data-testid="disc-background"
                />
                <MicDictation
                  value={form.background_context}
                  onTranscript={setField("background_context")}
                  testId="disc-background-mic"
                />
              </div>
            </Field>

            <Field label="What do you want to know?" hint="Optional. A direct question focuses the discernment.">
              <div className="relative">
                <input
                  type="text"
                  value={form.user_question}
                  onChange={update("user_question")}
                  className="rm-input w-full pr-12"
                  placeholder="Is Jessica right that Jake has to go tonight?"
                  data-testid="disc-question"
                />
                <MicDictation
                  value={form.user_question}
                  onTranscript={setField("user_question")}
                  testId="disc-question-mic"
                />
              </div>
            </Field>

            <Field
              label="Restoration Matrix packet context (optional)"
              hint="Upload your own packet so the discernment reads the conflict through your ladder. Optionally upload the other party's packet as well — only with their consent."
            >
              <div className="space-y-3">
                <div className="rm-disc-pkt-row">
                  <label className="rm-disc-pkt-label" htmlFor="disc-your-packet">
                    Your packet
                  </label>
                  <input
                    id="disc-your-packet"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setYourPacket(e.target.files?.[0] || null)}
                    className="rm-disc-pkt-input"
                    data-testid="disc-your-packet-input"
                  />
                  {yourPacket ? (
                    <button
                      type="button"
                      onClick={() => setYourPacket(null)}
                      className="rm-disc-pkt-clear"
                      data-testid="disc-your-packet-clear"
                      aria-label="Remove your packet"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
                <div className="rm-disc-pkt-row">
                  <label className="rm-disc-pkt-label" htmlFor="disc-their-packet">
                    Their packet
                  </label>
                  <input
                    id="disc-their-packet"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setTheirPacket(e.target.files?.[0] || null)}
                    className="rm-disc-pkt-input"
                    data-testid="disc-their-packet-input"
                  />
                  {theirPacket ? (
                    <button
                      type="button"
                      onClick={() => { setTheirPacket(null); setTheirConsent(false); }}
                      className="rm-disc-pkt-clear"
                      data-testid="disc-their-packet-clear"
                      aria-label="Remove their packet"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
                {theirPacket ? (
                  <label className="flex items-start gap-2 text-xs text-[#F0E6D2]/75 mt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={theirConsent}
                      onChange={(e) => setTheirConsent(e.target.checked)}
                      className="mt-[3px] accent-[#FFE7A0]"
                      data-testid="disc-their-consent"
                    />
                    <span>
                      I affirm that the other party shared this packet with me
                      willingly. We are using it together to seek discernment about
                      our relationship.
                    </span>
                  </label>
                ) : null}
              </div>
            </Field>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className={`rm-btn ${submitting ? "opacity-60 cursor-wait" : "rm-breathe"}`}
                data-testid="disc-submit"
              >
                {submitting ? "Discerning…" : "Run the Discernment"}
              </button>
              {submitting ? (
                <div className="mt-6" data-testid="disc-progress-wrap">
                  <PastoralProgress
                    phase={serverPhase || "Reading the situation you brought…"}
                    testIdPrefix="disc-progress"
                  />
                </div>
              ) : (
                <div className="mt-3 text-xs text-[#F0E6D2]/65 font-cinzel uppercase tracking-[0.2em]">
                  Typically 30–60 seconds
                </div>
              )}
            </div>
          </form>
        </motion.div>
      </section>
    </main>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block mb-2 font-cinzel text-xs sm:text-sm tracking-[0.2em] uppercase text-[#FFE7A0]/90">
        {label} {required ? <span className="text-[#FFE7A0]">*</span> : null}
      </label>
      {children}
      {hint ? (
        <div className="mt-2 text-xs sm:text-sm text-[#F0E6D2]/60 font-body font-light leading-snug">
          {hint}
        </div>
      ) : null}
    </div>
  );
}
